import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import Loader from "../../components/Loader";
import Calling from "../../api/Calling";
import { useNavigate } from "react-router-dom";
import {
  LuGhost,
  LuSave,
  LuHash,
  LuSmartphone,
  LuKeyRound,
  LuAppWindow,
  LuGlobe,
  LuEye,
  LuEyeOff,
  LuFlaskConical,
  LuShieldCheck,
  LuCircleCheck,
  LuCircleX,
  LuCircleAlert,
  LuInfo,
  LuLoaderCircle,
  LuPower,
  LuPlus,
  LuTrash2,
} from "react-icons/lu";

/* ---------------- Reusable presentational pieces (match Controller styling) --------------- */

const Toggle = ({ checked, onChange }) => (
  <label className="relative inline-flex items-center cursor-pointer shrink-0">
    <input type="checkbox" checked={checked} onChange={onChange} className="sr-only peer" />
    <div className="w-12 h-6 bg-black/15 rounded-full peer-focus:ring-4 peer-focus:ring-teal-400/30 peer-checked:bg-teal-500 transition-colors duration-300 relative">
      <div
        className={`absolute top-1 left-1 w-4 h-4 rounded-full bg-white shadow-sm transition-transform duration-300 ${
          checked ? "translate-x-6" : "translate-x-0"
        }`}
      />
    </div>
  </label>
);

const SectionHeader = ({ icon, title, desc, action }) => (
  <div className="flex items-center gap-3 mb-5">
    <div className="w-9 h-9 rounded-xl bg-teal-500/15 text-brand-teal flex items-center justify-center shrink-0">
      {icon}
    </div>
    <div className="flex-1 min-w-0">
      <h2 className="text-base sm:text-lg font-bold text-main-font leading-tight">{title}</h2>
      {desc && <p className="text-xs text-main-subfont mt-0.5">{desc}</p>}
    </div>
    {action}
  </div>
);

const Field = ({ icon, label, hint, children }) => (
  <div className="rounded-xl bg-white border border-main-border p-3.5">
    <div className="flex items-center gap-2.5 mb-2">
      <span className="w-8 h-8 rounded-lg bg-teal-500/15 text-brand-teal flex items-center justify-center shrink-0">
        {icon}
      </span>
      <label className="font-semibold text-main-font text-sm">{label}</label>
    </div>
    {children}
    {hint && <p className="text-[11px] text-main-subfont mt-1.5 leading-relaxed">{hint}</p>}
  </div>
);

// A fresh, blank account row. `appId` defaults to whatever another account already uses (usually the
// same app across every ad account).
const blankAccount = (appId = "") => ({
  id: null,
  label: "",
  enabled: true,
  snapAppId: "",
  appId,
  pixelId: "",
  testEvent: true,
  capiToken: "", // new value typed by the admin (blank = keep saved)
  tokenSet: false,
  tokenPreview: "",
  _show: false,
  _testing: false,
  _result: null,
});

/* ---------------- One ad-account card --------------- */

const AccountCard = ({ acc, index, onChange, onRemove, onTest, canRemove }) => {
  const set = (patch) => onChange(index, patch);
  return (
    <div className="ui-card-solid p-4 sm:p-5">
      {/* Card header: name + enable + test + remove */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <span className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-500 text-white flex items-center justify-center shrink-0 font-bold">
            {index + 1}
          </span>
          <input
            type="text"
            value={acc.label}
            onChange={(e) => set({ label: e.target.value })}
            className="ui-input w-full px-3 py-2 rounded-lg font-semibold"
            placeholder={`Account ${index + 1} name (e.g. Main / Agency X)`}
          />
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-white border border-main-border">
            <span className={`text-xs font-semibold ${acc.enabled ? "text-brand-teal" : "text-main-subfont"}`}>
              {acc.enabled ? "On" : "Off"}
            </span>
            <Toggle checked={acc.enabled} onChange={() => set({ enabled: !acc.enabled })} />
          </div>
          <button
            onClick={() => onTest(index)}
            disabled={acc._testing}
            className="px-3.5 py-2 bg-white text-brand-teal border border-brand-teal/40 font-bold rounded-lg hover:bg-teal-500/5 transition flex items-center gap-1.5 disabled:opacity-60"
          >
            {acc._testing ? <LuLoaderCircle className="animate-spin" /> : <LuFlaskConical />}
            {acc._testing ? "Testing…" : "Test"}
          </button>
          <button
            onClick={() => onRemove(index)}
            disabled={!canRemove}
            title={canRemove ? "Remove account" : "Keep at least one account"}
            className="w-9 h-9 flex items-center justify-center rounded-lg bg-white border border-red-400/40 text-red-600 hover:bg-red-500/5 transition disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <LuTrash2 />
          </button>
        </div>
      </div>

      {/* Credentials */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field icon={<LuHash className="text-base" />} label="Snap App ID" hint="Snap App ID from this account's Ads Manager (required for a mobile app).">
          <input
            type="text"
            value={acc.snapAppId}
            onChange={(e) => set({ snapAppId: e.target.value })}
            className="ui-input w-full px-3 py-2.5 rounded-lg"
            placeholder="e.g. ffe9ab18-e550-4858-8246-2f7090db364e"
          />
        </Field>

        <Field icon={<LuSmartphone className="text-base" />} label="App Package Name" hint="Android package name — usually the same for every account.">
          <input
            type="text"
            value={acc.appId}
            onChange={(e) => set({ appId: e.target.value })}
            className="ui-input w-full px-3 py-2.5 rounded-lg"
            placeholder="e.g. com.company.app"
          />
        </Field>

        <Field
          icon={<LuKeyRound className="text-base" />}
          label="CAPI Access Token"
          hint={
            acc.tokenSet
              ? `Saved (${acc.tokenPreview}). Leave blank to keep it, or paste a new one to replace it.`
              : "Paste this account's Conversions API access token (from its Organization)."
          }
        >
          <div className="relative">
            <input
              type={acc._show ? "text" : "password"}
              value={acc.capiToken}
              onChange={(e) => set({ capiToken: e.target.value })}
              className="ui-input w-full px-3 py-2.5 pr-11 rounded-lg font-mono text-xs"
              placeholder={acc.tokenSet ? "•••••••• (saved — leave blank to keep)" : "Paste access token"}
              autoComplete="off"
            />
            <button
              type="button"
              onClick={() => set({ _show: !acc._show })}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-main-subfont hover:text-brand-teal transition"
              tabIndex={-1}
            >
              {acc._show ? <LuEyeOff /> : <LuEye />}
            </button>
          </div>
        </Field>

        <Field icon={<LuAppWindow className="text-base" />} label="Snap Pixel ID" hint="Web pixel only — leave blank for a mobile app.">
          <input
            type="text"
            value={acc.pixelId}
            onChange={(e) => set({ pixelId: e.target.value })}
            className="ui-input w-full px-3 py-2.5 rounded-lg"
            placeholder="Optional (web only)"
          />
        </Field>
      </div>

      {/* Test Event Mode */}
      <div className="mt-3 rounded-xl bg-white border border-main-border p-3.5 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <span className="w-8 h-8 rounded-lg bg-teal-500/15 text-brand-teal flex items-center justify-center shrink-0">
            <LuFlaskConical className="text-base" />
          </span>
          <div className="min-w-0">
            <label className="font-semibold text-main-font text-sm block">Test Event Mode</label>
            <p className="text-[11px] text-main-subfont mt-0.5 leading-relaxed">
              Events go to Events Manager → Test Events (NOT credited). Turn OFF for live conversions.
            </p>
          </div>
        </div>
        <Toggle checked={acc.testEvent} onChange={() => set({ testEvent: !acc.testEvent })} />
      </div>

      {/* Per-account test result */}
      {acc._result && (
        <div
          className={`mt-3 rounded-2xl border p-4 ${
            acc._result.ready ? "border-green-400/40 bg-green-500/5" : "border-red-400/40 bg-red-500/5"
          }`}
        >
          <div className="flex items-center gap-2.5 mb-3">
            {acc._result.ready ? (
              <LuCircleCheck className="text-green-600 text-2xl shrink-0" />
            ) : (
              <LuCircleX className="text-red-600 text-2xl shrink-0" />
            )}
            <p className={`font-bold text-sm ${acc._result.ready ? "text-green-700" : "text-red-700"}`}>
              {acc._result.message}
            </p>
          </div>

          {Array.isArray(acc._result.tokenProblems) && acc._result.tokenProblems.length > 0 && (
            <div className="mb-3 flex items-start gap-2 text-xs text-amber-700 bg-amber-500/10 border border-amber-400/30 rounded-lg p-2.5">
              <LuCircleAlert className="shrink-0 mt-0.5" />
              <span>Token: {acc._result.tokenProblems.join("; ")}</span>
            </div>
          )}

          {Array.isArray(acc._result.checks) && (
            <div className="space-y-2">
              {acc._result.checks.map((c, i) => (
                <div key={i} className="rounded-xl bg-white border border-main-border p-3">
                  <div className="flex items-center gap-2">
                    {c.ok ? (
                      <LuCircleCheck className="text-green-600 shrink-0" />
                    ) : (
                      <LuCircleX className="text-red-600 shrink-0" />
                    )}
                    <span className="font-semibold text-main-font text-sm">{c.label}</span>
                    <span className="ml-auto text-[11px] font-mono text-main-subfont">HTTP {c.http}</span>
                  </div>
                  <p className="text-xs text-main-subfont mt-1.5">{c.message}</p>
                  {c.response && (
                    <pre className="mt-2 text-[10px] leading-relaxed text-main-subfont bg-main-bg border border-main-border rounded-lg p-2 overflow-x-auto whitespace-pre-wrap break-words">
                      {c.response}
                    </pre>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ---------------- Page --------------- */

const SnapPixel = () => {
  const [loader, setLoader] = useState(false);
  const [saving, setSaving] = useState(false);

  const [enabled, setEnabled] = useState(true); // global master switch
  const [capiUrl, setCapiUrl] = useState(""); // global endpoint override
  const [accounts, setAccounts] = useState([]);

  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    setLoader(true);
    try {
      const resp = await Calling.GetSnapConfig();
      const { status, message, data } = resp;
      if (status && data) {
        setEnabled(data.enabled !== false);
        setCapiUrl(data.capiUrl || "");
        const list = (data.accounts || []).map((a) => ({
          id: a.id,
          label: a.label || "",
          enabled: a.enabled !== false,
          snapAppId: a.snapAppId || "",
          appId: a.appId || "",
          pixelId: a.pixelId || "",
          testEvent: !!a.testEvent,
          capiToken: "",
          tokenSet: !!a.capiTokenSet,
          tokenPreview: a.capiTokenPreview || "",
          _show: false,
          _testing: false,
          _result: null,
        }));
        setAccounts(list.length ? list : [blankAccount()]);
      } else {
        toast.error(message || "Failed to load Snap config");
      }
    } catch (error) {
      console.error("Snap fetch error:", error);
      navigate("/");
    } finally {
      setLoader(false);
    }
  };

  const updateAccount = (idx, patch) =>
    setAccounts((prev) => prev.map((a, i) => (i === idx ? { ...a, ...patch } : a)));

  const addAccount = () =>
    setAccounts((prev) => [...prev, blankAccount(prev[0]?.appId || "")]);

  const removeAccount = (idx) =>
    setAccounts((prev) => prev.filter((_, i) => i !== idx));

  const buildBody = () => ({
    enabled,
    capiUrl: capiUrl.trim(),
    accounts: accounts.map((a) => ({
      id: a.id || undefined,
      label: a.label.trim(),
      enabled: a.enabled,
      snapAppId: a.snapAppId.trim(),
      appId: a.appId.trim(),
      pixelId: a.pixelId.trim(),
      testEvent: a.testEvent,
      // Send the token only when a new one was typed; blank keeps the saved secret.
      ...(a.capiToken.trim() ? { capiToken: a.capiToken.trim() } : {}),
    })),
  });

  const handleSave = async () => {
    // Each account must have a Snap App ID (mobile) or a Pixel ID (web).
    const bad = accounts.findIndex((a) => !a.snapAppId.trim() && !a.pixelId.trim());
    if (bad !== -1) {
      return toast.error(`Account ${bad + 1}: enter a Snap App ID (mobile app) or a Pixel ID (web).`);
    }
    setSaving(true);
    try {
      const resp = await Calling.UpdateSnapConfig(buildBody());
      if (resp?.status) {
        toast.success("Snap config saved");
        fetchData();
      } else {
        toast.error(resp?.message || "Failed to save");
      }
    } catch (error) {
      console.error("Snap save error:", error);
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async (idx) => {
    const a = accounts[idx];
    updateAccount(idx, { _testing: true, _result: null });
    try {
      const resp = await Calling.TestSnapConfig({
        accountId: a.id || undefined,
        capiToken: a.capiToken.trim(),
        snapAppId: a.snapAppId.trim(),
        appId: a.appId.trim(),
        pixelId: a.pixelId.trim(),
      });
      updateAccount(idx, { _result: resp });
      if (resp?.ready) toast.success(`Account ${idx + 1} is ready — verified`);
      else toast.error(resp?.message || `Account ${idx + 1} test did not pass`);
    } catch (error) {
      console.error("Snap test error:", error);
      toast.error("Test failed to run");
    } finally {
      updateAccount(idx, { _testing: false });
    }
  };

  const activeCount = accounts.filter((a) => a.enabled && (a.snapAppId.trim() || a.pixelId.trim())).length;

  return (
    <>
      {loader ? (
        <Loader loader={loader} />
      ) : (
        <div className="md:ml-56 min-h-screen px-3 sm:px-4 md:px-6 pb-10 pt-20">
          {/* Sticky header */}
          <div className="sticky top-16 z-30 mb-5">
            <div className="ui-card-solid px-4 sm:px-5 py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-center gap-3 sm:gap-4">
                <div className="flex items-center justify-center w-11 h-11 rounded-2xl bg-gradient-to-br from-teal-500 to-cyan-500 text-white shadow-lg shadow-teal-500/30 shrink-0">
                  <LuGhost className="text-xl" />
                </div>
                <div>
                  <h1 className="text-xl sm:text-2xl font-extrabold text-main-font leading-tight">Snap Pixel</h1>
                  <p className="text-main-subfont text-xs sm:text-sm">
                    Snapchat Conversions API — multi-account app install tracking
                  </p>
                </div>
              </div>
              <button
                onClick={handleSave}
                disabled={saving}
                className="justify-center px-6 py-3 bg-gradient-to-r from-teal-500 to-cyan-500 text-white font-bold rounded-xl shadow-lg shadow-teal-500/30 hover:brightness-110 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-300 flex items-center gap-2 disabled:opacity-60"
              >
                <LuSave className="text-lg" />
                {saving ? "Saving…" : "Save All"}
              </button>
            </div>
          </div>

          {/* Info banner */}
          <div className="ui-card mb-6 p-4 flex gap-3">
            <LuInfo className="text-brand-teal text-xl shrink-0 mt-0.5" />
            <p className="text-sm text-main-subfont leading-relaxed">
              Add one entry per Snapchat ad account. On every install the server fires the SAME{" "}
              <span className="font-semibold text-main-font">APP&nbsp;INSTALL</span> event to every{" "}
              <span className="font-semibold text-main-font">enabled</span> account in parallel — each
              attributes independently, and Snapchat de-dupes on the shared event id. Each account needs
              its own <span className="font-semibold text-main-font">token + Snap App ID</span> (a CAPI
              token only works inside the Organization it was generated in). Tokens are stored server-side
              and never reach the app. Use each card's <span className="font-semibold text-main-font">Test</span>{" "}
              to verify it against Snapchat without sending a real conversion.
            </p>
          </div>

          <div className="space-y-6">
            {/* Master switch */}
            <SectionHeader
              icon={<LuPower className="text-lg" />}
              title="Tracking Status"
              desc="Master switch — turns every account on or off"
            />
            <div className="flex items-center justify-between gap-3 rounded-xl bg-white border border-main-border p-3.5 -mt-3">
              <div className="flex items-center gap-3 min-w-0">
                <span className="w-10 h-10 rounded-xl bg-teal-500/15 text-brand-teal flex items-center justify-center shrink-0">
                  <LuShieldCheck className="text-lg" />
                </span>
                <div className="min-w-0">
                  <span className="font-semibold text-main-font text-sm block">Install Tracking</span>
                  <span className={`text-xs font-medium ${enabled ? "text-brand-teal" : "text-red-600"}`}>
                    {enabled
                      ? `Enabled — ${activeCount} account${activeCount === 1 ? "" : "s"} will receive installs`
                      : "Disabled"}
                  </span>
                </div>
              </div>
              <Toggle checked={enabled} onChange={() => setEnabled((s) => !s)} />
            </div>

            {/* Accounts */}
            <SectionHeader
              icon={<LuKeyRound className="text-lg" />}
              title="Ad Accounts"
              desc="One Snapchat ad account per card"
              action={
                <button
                  onClick={addAccount}
                  className="px-4 py-2 bg-white text-brand-teal border border-brand-teal/40 font-bold rounded-xl hover:bg-teal-500/5 transition flex items-center gap-1.5 shrink-0"
                >
                  <LuPlus /> Add Account
                </button>
              }
            />

            <div className="space-y-4 -mt-2">
              {accounts.map((acc, idx) => (
                <AccountCard
                  key={acc.id || `new-${idx}`}
                  acc={acc}
                  index={idx}
                  onChange={updateAccount}
                  onRemove={removeAccount}
                  onTest={handleTest}
                  canRemove={accounts.length > 1}
                />
              ))}
            </div>

            {/* Advanced (global) */}
            <SectionHeader
              icon={<LuGlobe className="text-lg" />}
              title="Advanced"
              desc="Applies to every account"
            />
            <div className="-mt-2">
              <Field
                icon={<LuGlobe className="text-base" />}
                label="CAPI Endpoint URL"
                hint="Leave blank to use the default v2 mobile-app conversion endpoint."
              >
                <input
                  type="text"
                  value={capiUrl}
                  onChange={(e) => setCapiUrl(e.target.value)}
                  className="ui-input w-full px-3 py-2.5 rounded-lg"
                  placeholder="https://tr.snapchat.com/v2/conversion"
                />
              </Field>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SnapPixel;
